import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import db from '@/db';
import { formatCurrency } from '@/lib/formatters';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';
import { Vault, FileText, X } from 'lucide-react';

/**
 * Mini-widget flutuante: mostra nº de comandas abertas e ticket médio do dia.
 * Aparece apenas em rotas operacionais (caixa, garcom, cozinha).
 */
const ROUTES_VISIBLE = ['/caixa', '/garcom', '/cozinha', '/balanca', '/historico-caixa', '/painel'];

export default function ShiftWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [stats, setStats] = useState({ openCount: 0, avgTicket: 0, totalDay: 0 });

  const refresh = async () => {
    try {
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const startISO = startOfDay.toISOString();

      const [openCmds, paidToday] = await Promise.all([
        db.commands.where('status').equals('open').toArray(),
        db.commands.where('status').equals('paid').toArray(),
      ]);

      const activeOpen = openCmds.filter((c) => !c.archivedAt).length;
      const todaysPaid = paidToday.filter((c) => (c.paidAt || c.closedAt) && (c.paidAt || c.closedAt) >= startISO);
      const totalDay = todaysPaid.reduce((a, c) => a + Number(c.finalTotal ?? c.total ?? 0), 0);
      const avgTicket = todaysPaid.length > 0 ? totalDay / todaysPaid.length : 0;

      setStats({ openCount: activeOpen, avgTicket, totalDay, paidCount: todaysPaid.length });
    } catch {}
  };

  useEffect(() => { refresh(); }, []);
  useVisiblePolling(refresh, 8000);

  const visible = ROUTES_VISIBLE.some((r) => location.pathname.startsWith(r));
  if (!visible || !open) return null;

  return (
    <div
      data-testid="shift-widget"
      className="fixed bottom-4 right-4 z-30 bg-white border-2 border-slate-200 rounded-2xl shadow-lg overflow-hidden hidden md:block"
    >
      <div className="px-3 py-1.5 bg-slate-900 text-white flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold">Turno</span>
        <button
          data-testid="shift-widget-close"
          onClick={() => setOpen(false)}
          className="text-white/60 hover:text-white"
          title="Esconder"
        >
          <X size={12} />
        </button>
      </div>
      <div className="px-4 py-3 grid grid-cols-2 gap-3 min-w-[220px]">
        <div data-testid="shift-open-count">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-amber-600">
            <FileText size={10} /> Abertas
          </div>
          <p className="font-mono text-lg font-black text-slate-900 mt-0.5">{stats.openCount}</p>
        </div>
        <div data-testid="shift-avg-ticket">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-emerald-600">
            <Vault size={10} /> Ticket médio
          </div>
          <p className="font-mono text-lg font-black text-slate-900 mt-0.5">{formatCurrency(stats.avgTicket)}</p>
        </div>
        <div className="col-span-2 pt-2 border-t border-slate-100 flex justify-between items-center text-[10px]">
          <span className="text-slate-400">Hoje</span>
          <span className="font-mono font-bold text-slate-700">
            {stats.paidCount || 0} venda(s) • {formatCurrency(stats.totalDay)}
          </span>
        </div>
      </div>
    </div>
  );
}
